if (!customElements.get('multi-add-summary')) {
  class MultiAddSummary extends HTMLElement {
    static EVENTS = {
      change: 'multi-add-summary:change',
      multiAddChange: 'multi-add-to-cart:change',
      quantityOfferChange: 'quantity-offer:change',
      variantChange: 'variant:change',
    };

    static SELECTORS = { quantityOffer: 'quantity-offer' };

    static QUANTITY_FIELD = 'quantity';

    connectedCallback () {
      this._abort = new AbortController();
      this._totalEl = this.querySelector('.multi-add-summary__total');
      this._compareEl = this.querySelector('.multi-add-summary__compare');
      this._badgeEl = this.querySelector('.multi-add-summary__badge');
      this._savingsEl = this.querySelector('.multi-add-summary__savings');
      this._shimmerEl = this.querySelector('.multi-add-summary__shimmer');

      this._formId = this.getAttribute('form');
      this._basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this._baseCompare = parseInt(this.dataset.baseComparePrice, 10) || this._basePrice;
      this._baseVariantId = this.dataset.baseVariantId || null;

      this._linesBySource = new Map();
      this._totals = null;

      // form.elements also covers the selector input linked via the `form` attribute, outside the <form>.
      const qtyField = document.getElementById(this._formId)?.elements.namedItem(MultiAddSummary.QUANTITY_FIELD);
      this._qtyInput = qtyField instanceof HTMLInputElement ? qtyField : null;
      this._quantityOffer = [...document.querySelectorAll(MultiAddSummary.SELECTORS.quantityOffer)]
        .find(el => el.formId === this._formId) ?? null;

      const opts = { signal: this._abort.signal };
      const { EVENTS } = MultiAddSummary;
      document.addEventListener(EVENTS.multiAddChange, (e) => this._handleChange(e), opts);
      document.addEventListener(EVENTS.variantChange, (e) => this._handleVariantChange(e), opts);
      document.addEventListener(EVENTS.quantityOfferChange, (e) => this._handleQuantityOfferChange(e), opts);
      this._qtyInput?.addEventListener('input', () => this._update(), opts);
      this._qtyInput?.addEventListener('change', () => this._update(), opts);

      // Seed initial state from any multi-add-to-cart already connected
      // (timing: they may have dispatched their initial event before we connected)
      this._seedInitialState();
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    // Last computed totals in cents, for consumers that connect after the first paint (e.g. alma-widget).
    getTotal () {
      return this._totals;
    }

    _seedInitialState () {
      // Sources of `multi-add-to-cart:change` lines we need to pull from on
      // first paint: the multi_add_to_cart blocks (sommier / complementary)
      // AND the offer_pack block (its single/pack card carries the discount
      // applied to the main variant, e.g. -10€ on 1 soutien-gorge).
      const tagsPresent = new Set();
      const existing = [];
      document.querySelectorAll('multi-add-to-cart, offer-pack').forEach(el => {
        tagsPresent.add(el.localName);
        existing.push(el);
      });

      if (existing.length === 0) {
        this._update();
        return;
      }

      const doSeed = () => {
        existing.forEach(el => {
          if (typeof el.getLines === 'function') {
            this._linesBySource.set(el, el.getLines());
          }
        });
        this._update();
      };

      // Wait only for custom elements that ACTUALLY EXIST in the DOM —
      // `whenDefined` for a name that's never registered hangs forever.
      const wait = window.customElements?.whenDefined?.bind(window.customElements);
      if (!wait) {
        doSeed();
        return;
      }
      const pending = [...tagsPresent].map(name => wait(name).catch(() => null));
      Promise.all(pending).then(doSeed);
    }

    _handleVariantChange (event) {
      if (this._formId && event.detail?.formId !== this._formId) return;

      const variant = event.detail?.variant;
      if (!variant) return;

      this._basePrice = variant.price || 0;
      this._baseCompare = variant.compare_at_price || variant.price || 0;
      this._baseVariantId = String(variant.id);

      this._update();
    }

    // Re-rendered on variant change, so keep the latest instance.
    _handleQuantityOfferChange (event) {
      if (event.detail?.formId !== this._formId) return;
      this._quantityOffer = event.target;
      this._update();
    }

    _currentQty () {
      return Math.max(1, parseInt(this._qtyInput?.value, 10) || 1);
    }

    // Quantity-tier price ("-25% les 2") wins over the solo base price.
    _baseUnitPrice (qty) {
      return this._quantityOffer?.unitPriceFor(qty) ?? this._basePrice;
    }

    _handleChange (event) {
      const lines = event.detail?.lines || [];
      this._linesBySource.set(event.target, lines);
      this._update();
    }

    _update () {
      let baseQty = this._currentQty(); // overridden when a multi-add block represents the main variant
      let suppressBase = false; // flipped by a `replacesBase` line (single-offer discount)
      let complementaryTotal = 0;
      let complementaryCompare = 0;

      this._linesBySource.forEach(lines => {
        lines.forEach(line => {
          if (this._baseVariantId && String(line.variantId) === String(this._baseVariantId)) {
            // The mattress switcher acts as a qty selector for the main product
            baseQty = line.qty;
          } else {
            if (line.replacesBase) suppressBase = true;
            const lineCompare = (line.compareAtPrice != null ? line.compareAtPrice : line.price);
            complementaryCompare += lineCompare * line.qty;
            complementaryTotal += this._applyLineDiscount(line);
          }
        });
      });

      const effectiveBaseQty = suppressBase ? 0 : baseQty;
      const total = this._baseUnitPrice(effectiveBaseQty) * effectiveBaseQty + complementaryTotal;
      const compareTotal = this._baseCompare * effectiveBaseQty + complementaryCompare;

      this._dispatchChange(total, compareTotal);

      if (total === 0) {
        this.hidden = true;
        this._revealReadyState();
        return;
      }

      this.hidden = false;
      this._totalEl.textContent = this._formatMoney(total);

      const savings = compareTotal - total;
      const hasSavings = savings > 0;

      this._compareEl.hidden = !hasSavings;
      this._badgeEl.hidden = !hasSavings;

      if (hasSavings) {
        this._compareEl.textContent = this._formatMoney(compareTotal);
        this._savingsEl.textContent = this._formatMoney(savings);
      }

      this._revealReadyState();
    }

    _dispatchChange (total, compareTotal) {
      this._totals = { total, compareTotal };
      this.dispatchEvent(new CustomEvent(MultiAddSummary.EVENTS.change, {
        bubbles: true,
        detail: { total, compareTotal, formId: this._formId }
      }));
    }

    // Swap the shimmer placeholder for the real total/compare spans once the
    // first computation has finalized. Idempotent — safe to call repeatedly.
    _revealReadyState () {
      if (!this.hasAttribute('data-shimmering')) return;
      this.removeAttribute('data-shimmering');
      if (this._shimmerEl) this._shimmerEl.hidden = true;
      if (this._totalEl) this._totalEl.hidden = false;
    }

    _applyLineDiscount (line) {
      const lineFull = line.price * line.qty;
      // Explicit final price (offer-pack): `price` is already the card's
      // discounted total, so no discount math is needed.
      if (line.compareAtPrice != null) return lineFull;
      if (!line.discount) return lineFull;

      // No clamp: in main-share mode the line delta can legitimately go
      // negative (sommier + matelas bundle), and that negative must reduce
      // the summary total — clamping to 0 would hide the bundle savings.
      if (line.discountUnit === '%') {
        if (line.mainSharePrice != null) {
          // delta = M × (1 − pair_d/100) − S × (pair_d − solo_d)/100
          const complementaryShare = Math.round(lineFull * (100 - line.discount) / 100);
          const bonusPP = Math.max(0, line.discount - (line.soloDiscount || 0));
          const mainBonusCredit = Math.round(line.mainSharePrice * bonusPP / 100);
          return complementaryShare - mainBonusCredit;
        }
        return Math.round(lineFull * (1 - line.discount / 100));
      }
      return lineFull - line.discount * 100;
    }

    _formatMoney (cents) {
      const fmt = window.themeVariables?.settings?.moneyFormat
        || window.theme?.moneyFormat
        || window.Shopify?.money_format;

      if (window.Shopify?.formatMoney && fmt) {
        // Strip trailing .00 / ,00 (handles "29,00 €" and "29.00 $")
        return window.Shopify.formatMoney(cents, fmt).replace(/([.,])00(\D|$)/, '$2').trim();
      }

      // Fallback — manual formatting without trailing zeros
      const amount = cents / 100;
      const hasCents = amount % 1 !== 0;
      return amount.toLocaleString('fr-FR', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2
      }) + '\u202f€';
    }
  }

  customElements.define('multi-add-summary', MultiAddSummary);
}
